import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import './ContactEditModal.css';
import { LanguageContext } from "../context/LanguageContext";

const ContactEditModal = ({ isOpen, onClose, userData, onSave }) => {
  const { language, texts } = useContext(LanguageContext);

  const [formData, setFormData] = useState({
    salutation: '',
    first_name: '',
    last_name: '',
    company_name: '',
    street: '',
    house_number: '',
    postal_code: '',
    town: '',
    country_id: '',
    phone_number: '',
  });

  const [salutations, setSalutations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const [salutRes, countryRes, userRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/salutations?lang=${language}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/countries?lang=${language}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/user/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setSalutations(salutRes.data);
        setCountries(countryRes.data);
        setFormData(userRes.data);

      } catch (err) {
        setError(texts.contactEdit.errorLoading);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, language, texts]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setIsDirty(true);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    const requiredFields = [
      'salutation', 'first_name', 'last_name', 'street',
      'house_number', 'postal_code', 'town', 'country_id', 'phone_number',
    ];
    return requiredFields.every(field => {
      const value = formData[field];
      return typeof value === 'string'
        ? value.trim() !== ''
        : value !== undefined && value !== null;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      setError(texts.contactEdit.requiredFields);
      return;
    }

    const token = localStorage.getItem('authToken');
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/user/update`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      onSave(formData);
      onClose();
    } catch (err) {
      console.error('Fehler beim Speichern der Daten:', err);
      setError(texts.contactEdit.errorSaving);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(texts.contactEdit.confirmLeave);
      if (confirmLeave) {
        handleSave(new Event('submit'));
      } else {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close-button" onClick={handleClose}>x</button>

        <h2 className="register-header">{texts.contactEdit.title}</h2>
        {error && <div className="error-message">{error}</div>}

        <form className="register-form" onSubmit={handleSave}>

          <div className="form-group">
            <label>{texts.contactEdit.salutation}</label>
            <select
              name="salutation"
              value={formData.salutation}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="">{texts.contactEdit.selectSalutation}</option>
              {salutations.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.company}</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.firstName}</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.lastName}</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.street}</label>
            <input
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.houseNumber}</label>
            <input
              type="text"
              name="house_number"
              value={formData.house_number}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.postalCode}</label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.town}</label>
            <input
              type="text"
              name="town"
              value={formData.town}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.country}</label>
            <select
              name="country_id"
              value={formData.country_id}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="">{texts.contactEdit.selectCountry}</option>
              {countries.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{texts.contactEdit.phone}</label>
            <input
              type="text"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="modal-buttons">
            <button type="button" onClick={handleClose}>
              {texts.contactEdit.cancel}
            </button>
            <button type="submit" disabled={!isFormValid()}>
              {texts.contactEdit.save}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ContactEditModal;
